// Where the announced-message box sits (v1.3 ticket 15, WCAG 2.4.11 Focus Not Obscured).
//
// The box is fixed at the bottom right of the window, over the page and over any open dialog. On a small window or at
// 200% zoom it can cover the control that has focus (it hid a dialog's Add button completely). So it moves to the top
// right while it would cover the focused control, and moves back only when it would cover the focused control there.
// It never jumps for a control it does not cover, and a control too tall to clear in either place leaves it alone.

/** True when two boxes ({ left, right, top, bottom }, CSS px) share any area */
const overlaps = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

/**
 * @param {object} p
 * @param {?object} p.focus the focused control's box (getBoundingClientRect)
 * @param {?object} p.box the message box's box where it is now
 * @param {number} p.viewportHeight the window's height
 * @param {boolean} p.atTop whether the box is at the top now
 * @param {number} [p.margin] its distance from the window's edge (Announcer.module.css: 10px)
 * @returns {boolean} whether the box belongs at the top
 */
export function messageBoxAtTop({ focus, box, viewportHeight, atTop, margin = 10 }) {
  if (!focus || !box || !overlaps(focus, box)) return atTop;
  const height = box.bottom - box.top;
  const elsewhere = atTop
    ? { left: box.left, right: box.right, top: viewportHeight - margin - height, bottom: viewportHeight - margin }
    : { left: box.left, right: box.right, top: margin, bottom: margin + height };
  return overlaps(focus, elsewhere) ? atTop : !atTop;
}

/**
 * The box a sighted user sees for a focused control. A native input kept visually hidden (1px, clipped) shows itself
 * through its <label> (the Supporting Document upload, ticket 07), a PrimeReact input through its component wrapper.
 * @param {Element} el the focused element
 */
export function shownBox(el) {
  const own = el.getBoundingClientRect();
  if (own.width > 1 && own.height > 1) return own;
  const standIn = (el.labels && el.labels[0]) || el.closest(".p-inputwrapper, .p-dropdown");
  return standIn ? standIn.getBoundingClientRect() : own;
}

/**
 * The area the message box's messages cover: the union of its parts that show something. The container itself is
 * taller than that when one region is empty (the flex gap beside the empty one), which would move the box for a
 * control it does not cover.
 * @param {Element} box the Announcer's container
 * @returns {?object} { left, right, top, bottom }, or null when no message shows
 */
export function messagesBox(box) {
  const parts = [...box.children].map((c) => c.getBoundingClientRect()).filter((r) => r.height > 0 && r.width > 0);
  if (!parts.length) return null;
  return {
    left: Math.min(...parts.map((r) => r.left)),
    right: Math.max(...parts.map((r) => r.right)),
    top: Math.min(...parts.map((r) => r.top)),
    bottom: Math.max(...parts.map((r) => r.bottom)),
  };
}
